export type InsulationLevelNumber = 1 | 2 | 3 | 4 | 5;

export type WallInsulationMapping = {
  cavityInsulation: string;
  boardInsulation: string;
};

export type SingleValueInsulationMapping = {
  insulationValue: string;
};

export type DualValueInsulationMapping = {
  primary: string;
  secondary: string;
};

export type InsulationLevelSettingsResponse = Record<
  string,
  Record<number, { primaryValue: string; secondaryValue: string | null }>
>;

export type ParsedInsulationLevelMaps = {
  walls: Record<InsulationLevelNumber, WallInsulationMapping>;
  attic: Record<InsulationLevelNumber, SingleValueInsulationMapping>;
  ceilingBelowRoof: Record<InsulationLevelNumber, DualValueInsulationMapping>;
  floor: Record<InsulationLevelNumber, DualValueInsulationMapping>;
};

export function parseInsulationLevelSettings(
  data: InsulationLevelSettingsResponse
): ParsedInsulationLevelMaps {
  const walls = {} as Record<InsulationLevelNumber, WallInsulationMapping>;
  const attic = {} as Record<
    InsulationLevelNumber,
    SingleValueInsulationMapping
  >;
  const ceilingBelowRoof = {} as Record<
    InsulationLevelNumber,
    DualValueInsulationMapping
  >;
  const floor = {} as Record<InsulationLevelNumber, DualValueInsulationMapping>;

  if (data.walls) {
    for (const [lvl, row] of Object.entries(data.walls)) {
      walls[Number(lvl) as InsulationLevelNumber] = {
        cavityInsulation: row.primaryValue,
        boardInsulation: row.secondaryValue || 'None'
      };
    }
  }

  if (data.attic) {
    for (const [lvl, row] of Object.entries(data.attic)) {
      attic[Number(lvl) as InsulationLevelNumber] = {
        insulationValue: row.primaryValue
      };
    }
  }

  if (data.ceilingBelowRoof) {
    for (const [lvl, row] of Object.entries(data.ceilingBelowRoof)) {
      ceilingBelowRoof[Number(lvl) as InsulationLevelNumber] = {
        primary: row.primaryValue,
        secondary: row.secondaryValue || 'None'
      };
    }
  }

  if (data.floor) {
    for (const [lvl, row] of Object.entries(data.floor)) {
      floor[Number(lvl) as InsulationLevelNumber] = {
        primary: row.primaryValue,
        secondary: row.secondaryValue || 'None'
      };
    }
  }

  return { walls, attic, ceilingBelowRoof, floor };
}

export function parseRValue(rValue: string): number {
  if (rValue === 'None') return 0;
  const match = rValue.match(/R-(\d+\.?\d*)/);
  return match ? parseFloat(match[1]) : 0;
}

export function matchWallLevel(
  cavityInsulation: string,
  boardInsulation: string,
  levels: Record<InsulationLevelNumber, WallInsulationMapping>
): InsulationLevelNumber | 'Custom' {
  for (const lvl of [1, 2, 3, 4, 5] as InsulationLevelNumber[]) {
    const mapping = levels[lvl];
    if (
      mapping &&
      mapping.cavityInsulation === cavityInsulation &&
      mapping.boardInsulation === boardInsulation
    ) {
      return lvl;
    }
  }
  return 'Custom';
}

export function matchSingleValueLevel(
  value: string,
  levels: Record<InsulationLevelNumber, SingleValueInsulationMapping>
): InsulationLevelNumber | 'Custom' {
  for (const lvl of [1, 2, 3, 4, 5] as InsulationLevelNumber[]) {
    if (levels[lvl] && levels[lvl].insulationValue === value) {
      return lvl;
    }
  }
  return 'Custom';
}

export function matchDualValueLevel(
  primary: string,
  secondary: string,
  levels: Record<InsulationLevelNumber, DualValueInsulationMapping>
): InsulationLevelNumber | 'Custom' {
  for (const lvl of [1, 2, 3, 4, 5] as InsulationLevelNumber[]) {
    const mapping = levels[lvl];
    if (
      mapping &&
      mapping.primary === primary &&
      mapping.secondary === secondary
    ) {
      return lvl;
    }
  }
  return 'Custom';
}
